export interface ModuleMastery {
  status: 'MASTERED' | 'IN_PROGRESS' | 'NEEDS_ATTENTION' | 'NOT_ASSESSED';
  scorePercent: number; // 0 to 100
  lastAssessedDate: string;
  notes: string;
}

export interface DemoStudent {
  id: string;
  name: string;
  rollId: string;
  age: number;
  grade: string;
  disabilityCategory: string;
  coOccurringDisabilityCategory?: string;
  guardianName: string;
  guardianPhone: string;
  therapist: string;
  studentEmail: string;
  studentPassword?: string;
  portalPin?: string;
  tenant?: string;
  iepStatus: 'ACTIVE' | 'PENDING_REVIEW' | 'COMPLETED';
  iepGoal: string;
  recentScore: string;
  overallMasteryPercent: number;
  clinicalSummary: string;
  aiRecommendation: string;
  moduleMasteryMap: Record<string, ModuleMastery>;
}

export const DEMO_STUDENTS: DemoStudent[] = [
  {
    id: '1',
    name: 'Master Rahul Sharma',
    rollId: 'STU-2026-001',
    age: 9,
    grade: 'Class 4-B',
    disabilityCategory: 'Autism Spectrum (ASD)',
    coOccurringDisabilityCategory: 'Speech & Articulation Impairment',
    guardianName: 'Sunita Sharma',
    guardianPhone: '+91 98230 11223',
    therapist: 'Sarah Jenkins (SLP)',
    studentEmail: 'rahul.sharma@bugfreeschool.org',
    studentPassword: 'Rahul#2026Password',
    portalPin: '1234',
    tenant: 'Sunshine Autism Academy',
    iepStatus: 'ACTIVE',
    iepGoal: 'Express 4-word sentences using AAC Core Grid with 85% accuracy',
    recentScore: 'VB-MAPP Score: 142/170 (Level 3 Milestone)',
    overallMasteryPercent: 86,
    clinicalSummary: 'Rahul demonstrates high engagement with AAC symbol grids and visual schedules. Shows significant progress in VB-MAPP Level 3 milestones.',
    aiRecommendation: 'Recommend introducing 5-word core vocabulary phrases in CoreVocabAIModule and continuing token rewards in AutismABAModule.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-28', notes: 'Expresses needs w/ symbol board' },
      'speech-therapy': { status: 'IN_PROGRESS', scorePercent: 78, lastAssessedDate: '2026-07-25', notes: 'Working on /r/ consonant blends' },
      'autism': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-29', notes: 'Token economy stars active' },
      'sign-language': { status: 'IN_PROGRESS', scorePercent: 70, lastAssessedDate: '2026-07-20', notes: 'Mastered 25 basic ISL signs' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A for ASD visual focus' },
      'student': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-08-01', notes: 'IEP Goal 3 of 4 met' },
      'therapist': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-30', notes: 'SOAP notes up to date' },
      'teacher': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-27', notes: 'Classroom visual supports ready' },
      'assessment': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-15', notes: 'Sensory Profile 2: 78/100' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Bus Route #4 assigned' },
      'hospital': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'Outpatient setting' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-29', notes: 'Parent photo log active' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-22', notes: 'AAC iPad assigned' },
      'analytics': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-08-01', notes: 'Executive BI chart active' },
      'ai': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-30', notes: 'AI summary generated' },
      'sensory-room': { status: 'MASTERED', scorePercent: 94, lastAssessedDate: '2026-07-26', notes: 'Snoezelen Lavender preset' },
      'tele-rehab': { status: 'IN_PROGRESS', scorePercent: 80, lastAssessedDate: '2026-07-24', notes: 'Weekly WebRTC sessions' },
      'early-intervention': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-10', notes: 'Milestones completed' },
      'vocational-living': { status: 'IN_PROGRESS', scorePercent: 75, lastAssessedDate: '2026-07-18', notes: 'Sorting tasks mastered' },
      'hydrotherapy': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'music-therapy': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-29', notes: 'RAS Metronome 72 BPM' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 96, lastAssessedDate: '2026-07-28', notes: 'Goldendoodle AAT active' },
      'prosthetics': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vbmapp-assessment': { status: 'MASTERED', scorePercent: 84, lastAssessedDate: '2026-07-25', notes: 'Level 3 Radar complete' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-29', notes: '30-button core grid' },
      'fim-weefim': { status: 'IN_PROGRESS', scorePercent: 76, lastAssessedDate: '2026-07-15', notes: 'Cognitive FIM 28/35' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-27', notes: '310ms reaction latency' },
      'transition-matrix': { status: 'IN_PROGRESS', scorePercent: 70, lastAssessedDate: '2026-07-20', notes: 'Vocational matrix 70%' }
    }
  },
  {
    id: '2',
    name: 'Miss Ananya Kulkarni',
    rollId: 'STU-2026-002',
    age: 7,
    grade: 'Class 2-A',
    disabilityCategory: 'Speech & Articulation Impairment',
    coOccurringDisabilityCategory: 'Expressive Language Delay',
    guardianName: 'Vikram Kulkarni',
    guardianPhone: '+91 98211 44556',
    therapist: 'Michael Chen (OT)',
    studentEmail: 'ananya.kulkarni@bugfreeschool.org',
    studentPassword: 'Ananya#2026Password',
    portalPin: '2345',
    tenant: 'Sunshine Autism Academy',
    iepStatus: 'ACTIVE',
    iepGoal: 'Master /r/ and /s/ phonetic articulation in initial position',
    recentScore: 'Articulation Precision: 92% Correct',
    overallMasteryPercent: 89,
    clinicalSummary: 'Ananya shows exceptional progress in sound frequency matching and phonetic repetition drills.',
    aiRecommendation: 'Continue /r/ consonant blend drills in SpeechTherapyModule and integrate auditory entrainment in MusicTherapyModule.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-30', notes: 'Fast symbol selection' },
      'speech-therapy': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-08-01', notes: '92% sound accuracy' },
      'autism': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'sign-language': { status: 'IN_PROGRESS', scorePercent: 82, lastAssessedDate: '2026-07-22', notes: 'Fingerspelling practice' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'student': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-08-01', notes: 'IEP Goal 92%' },
      'therapist': { status: 'MASTERED', scorePercent: 96, lastAssessedDate: '2026-07-31', notes: 'CPT 92507 billed' },
      'teacher': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-28', notes: 'Classroom visual supports' },
      'assessment': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-18', notes: 'CARS-2 score 31.0' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Bus Route #1' },
      'hospital': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 94, lastAssessedDate: '2026-07-30', notes: 'Daily parent updates' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-25', notes: 'SpeechMic assigned' },
      'analytics': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-08-01', notes: 'BI Report exported' },
      'ai': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-30', notes: 'AI letter written' },
      'sensory-room': { status: 'IN_PROGRESS', scorePercent: 80, lastAssessedDate: '2026-07-20', notes: 'Ocean Blue soundscape' },
      'tele-rehab': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-28', notes: 'WebRTC speech session' },
      'early-intervention': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-12', notes: 'Milestones met' },
      'vocational-living': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'hydrotherapy': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'music-therapy': { status: 'MASTERED', scorePercent: 94, lastAssessedDate: '2026-07-30', notes: 'Pentatonic keyboard pitch' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-29', notes: 'Rabbit AAT session' },
      'prosthetics': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vbmapp-assessment': { status: 'IN_PROGRESS', scorePercent: 78, lastAssessedDate: '2026-07-22', notes: 'Echoic milestones' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-30', notes: 'Core vocab active' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-18', notes: 'Cognitive FIM 32/35' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-28', notes: 'Reaction latency 290ms' },
      'transition-matrix': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' }
    }
  },
  {
    id: '3',
    name: 'Master Aarav Patel',
    rollId: 'STU-2026-003',
    age: 11,
    grade: 'Class 5-C',
    disabilityCategory: 'Cerebral Palsy (Motor Skills)',
    coOccurringDisabilityCategory: 'Dysarthria & AAC Communication Needs',
    guardianName: 'Rajesh Patel',
    guardianPhone: '+91 98900 77889',
    therapist: 'Priya Nair (PT)',
    studentEmail: 'aarav.patel@bugfreeschool.org',
    studentPassword: 'Aarav#2026Password',
    portalPin: '3456',
    tenant: 'Sunshine Autism Academy',
    iepStatus: 'PENDING_REVIEW',
    iepGoal: 'Complete 50m gait walk with 60% buoyancy in Hydrotherapy Pool',
    recentScore: 'FIM Motor Score: 68/91 (Independent w/ Device)',
    overallMasteryPercent: 78,
    clinicalSummary: 'Aarav exhibits strong motivation during hydrotherapy sessions and WebGL biofeedback drills. Needs ongoing myoelectric gain adjustments.',
    aiRecommendation: 'Increase hydrotherapy pool temperature to 35°C and continue 60% weight-bearing gait reduction in HydrotherapyModule.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-25', notes: 'Eye-gaze companion active' },
      'speech-therapy': { status: 'IN_PROGRESS', scorePercent: 72, lastAssessedDate: '2026-07-20', notes: 'Respiration & vocal fatigue' },
      'autism': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'sign-language': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'student': { status: 'IN_PROGRESS', scorePercent: 78, lastAssessedDate: '2026-08-01', notes: 'Pending annual review' },
      'therapist': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-30', notes: 'CPT 97110 physical therapy' },
      'teacher': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-26', notes: 'Slant board accommodation' },
      'assessment': { status: 'MASTERED', scorePercent: 80, lastAssessedDate: '2026-07-14', notes: 'Vineland-3 score 82' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Wheelchair van Route #4' },
      'hospital': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-10', notes: 'Inpatient rehab completed' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-29', notes: 'Daily therapy photo log' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-20', notes: 'Power wheelchair calibrated' },
      'analytics': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-08-01', notes: 'BI gait telemetry chart' },
      'ai': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-30', notes: 'PT summary generated' },
      'sensory-room': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-24', notes: 'Warm Amber light preset' },
      'tele-rehab': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-27', notes: 'Remote PT consultation' },
      'early-intervention': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-05', notes: 'Pediatric milestones' },
      'vocational-living': { status: 'IN_PROGRESS', scorePercent: 70, lastAssessedDate: '2026-07-16', notes: 'Adaptive kitchen task' },
      'hydrotherapy': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-31', notes: '34.5°C Pool temp 60% buoyancy' },
      'music-therapy': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-29', notes: 'RAS Metronome 72 BPM gait' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-28', notes: 'Therapy horse session' },
      'prosthetics': { status: 'MASTERED', scorePercent: 84, lastAssessedDate: '2026-07-25', notes: '85% EMG sensor gain' },
      'vbmapp-assessment': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-28', notes: 'AAC motor planning' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 75, lastAssessedDate: '2026-07-31', notes: 'Motor FIM 68/91' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'Caregiver EVV check-in' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 80, lastAssessedDate: '2026-07-27', notes: 'Target tracking score 420' },
      'transition-matrix': { status: 'IN_PROGRESS', scorePercent: 65, lastAssessedDate: '2026-07-18', notes: 'Transition goal 65%' }
    }
  },
  {
    id: '4',
    name: 'Miss Diya Sengupta',
    rollId: 'STU-2026-101',
    age: 6,
    grade: 'Class 1-A',
    disabilityCategory: 'Hearing Impairment',
    coOccurringDisabilityCategory: 'Vestibular Balance Impairment',
    guardianName: 'Rohan Sengupta',
    guardianPhone: '+91 98450 11990',
    therapist: 'Sarah Jenkins (SLP)',
    studentEmail: 'diya.sengupta@bugfreeschool.org',
    studentPassword: 'Diya#2026Password',
    portalPin: '4567',
    tenant: 'Sunshine Autism Academy',
    iepStatus: 'ACTIVE',
    iepGoal: 'Recognize 50 ISL sign language video flashcards with zero prompts',
    recentScore: 'ISL Sign Dictionary Mastery: 48/50 Signs',
    overallMasteryPercent: 94,
    clinicalSummary: 'Diya is highly expressive in Indian Sign Language (ISL) and visual fingerspelling.',
    aiRecommendation: 'Introduce advanced ISL sentence structure in SignLanguageModule and pair with visual AAC boards.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-28', notes: 'Visual symbol board' },
      'speech-therapy': { status: 'IN_PROGRESS', scorePercent: 75, lastAssessedDate: '2026-07-20', notes: 'Vocal pitch matching' },
      'autism': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'sign-language': { status: 'MASTERED', scorePercent: 96, lastAssessedDate: '2026-08-01', notes: '48/50 ISL signs mastered' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'student': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-08-01', notes: 'IEP Goal 94%' },
      'therapist': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-30', notes: 'SOAP note up to date' },
      'teacher': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-27', notes: 'Visual classroom signs' },
      'assessment': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-16', notes: 'Audiology assessment' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Bus Route #1' },
      'hospital': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-29', notes: 'Parent video message stream' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-22', notes: 'Hearing aid calibrated' },
      'analytics': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-08-01', notes: 'BI report active' },
      'ai': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-30', notes: 'AI summary written' },
      'sensory-room': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-25', notes: 'Forest Therapy Green' },
      'tele-rehab': { status: 'MASTERED', scorePercent: 94, lastAssessedDate: '2026-07-28', notes: 'Video sign consultation' },
      'early-intervention': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-08', notes: 'Early pediatric milestones' },
      'vocational-living': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'hydrotherapy': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'music-therapy': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-29', notes: 'Vibrational rhythmic beat' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-27', notes: 'Therapy rabbit session' },
      'prosthetics': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vbmapp-assessment': { status: 'IN_PROGRESS', scorePercent: 80, lastAssessedDate: '2026-07-21', notes: 'Visual-perceptual milestone' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-28', notes: 'Visual core vocab' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-16', notes: 'Cognitive FIM 33/35' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-26', notes: 'Visual tracking score 510' },
      'transition-matrix': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' }
    }
  },
  {
    id: '5',
    name: 'Master Ishan Verma',
    rollId: 'STU-2026-102',
    age: 8,
    grade: 'Class 3-B',
    disabilityCategory: 'Down Syndrome',
    coOccurringDisabilityCategory: 'Speech Apraxia & Low Tone',
    guardianName: 'Meera Verma',
    guardianPhone: '+91 98765 43210',
    therapist: 'Michael Chen (OT)',
    studentEmail: 'ishan.verma@bugfreeschool.org',
    studentPassword: 'Ishan#2026Password',
    portalPin: '5678',
    tenant: 'Sunrise Special Needs School',
    iepStatus: 'ACTIVE',
    iepGoal: 'Complete task analysis for handwashing and retail sorting independently',
    recentScore: 'Task Analysis Mastery: 95%',
    overallMasteryPercent: 87,
    clinicalSummary: 'Ishan responds very well to positive reinforcement token boards and structured task-analysis schedules.',
    aiRecommendation: 'Maintain token economy in AutismABAModule and advance independent living task steps in VocationalLivingModule.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-27', notes: 'Symbol board active' },
      'speech-therapy': { status: 'IN_PROGRESS', scorePercent: 76, lastAssessedDate: '2026-07-22', notes: 'Articulation /l/ and /k/' },
      'autism': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-30', notes: 'Token stars 5/5' },
      'sign-language': { status: 'IN_PROGRESS', scorePercent: 75, lastAssessedDate: '2026-07-18', notes: 'Basic gesture signs' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'student': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-08-01', notes: 'IEP Goal 87%' },
      'therapist': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-30', notes: 'OT session logged' },
      'teacher': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-26', notes: 'Lesson plan visual prompt' },
      'assessment': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-15', notes: 'Vineland-3 score 80' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Bus Route #1' },
      'hospital': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-28', notes: 'Parent messenger active' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-20', notes: 'Adaptive grip pencil' },
      'analytics': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-08-01', notes: 'BI outcome report' },
      'ai': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-30', notes: 'AI letter written' },
      'sensory-room': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-24', notes: 'Warm Sunset Amber' },
      'tele-rehab': { status: 'IN_PROGRESS', scorePercent: 80, lastAssessedDate: '2026-07-25', notes: 'Tele-OT session' },
      'early-intervention': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-06', notes: 'Developmental milestone' },
      'vocational-living': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-31', notes: '95% Task analysis' },
      'hydrotherapy': { status: 'IN_PROGRESS', scorePercent: 78, lastAssessedDate: '2026-07-22', notes: 'Aquatic balance drill' },
      'music-therapy': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-29', notes: 'RAS beat 72 BPM' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-28', notes: 'Goldendoodle AAT' },
      'prosthetics': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vbmapp-assessment': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-24', notes: 'VB-MAPP Level 2' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-28', notes: 'Core vocab grid' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 84, lastAssessedDate: '2026-07-16', notes: 'WeeFIM Score 84' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-27', notes: 'Neuro-rehab game' },
      'transition-matrix': { status: 'IN_PROGRESS', scorePercent: 70, lastAssessedDate: '2026-07-19', notes: 'Pre-vocational matrix' }
    }
  },
  {
    id: '6',
    name: 'Master Tanmay Roy',
    rollId: 'STU-2026-103',
    age: 12,
    grade: 'Class 6-A',
    disabilityCategory: 'Visual Impairment (Braille)',
    coOccurringDisabilityCategory: 'Spatial Orientation Challenge',
    guardianName: 'Kunal Roy',
    guardianPhone: '+91 98112 23344',
    therapist: 'Priya Nair (PT)',
    studentEmail: 'tanmay.roy@bugfreeschool.org',
    studentPassword: 'Tanmay#2026Password',
    portalPin: '6789',
    tenant: 'Sunrise Special Needs School',
    iepStatus: 'ACTIVE',
    iepGoal: 'Transcribe Grade 2 Nemeth Braille math equations using 6-dot cell',
    recentScore: 'Braille Reading Rate: 45 wpm',
    overallMasteryPercent: 91,
    clinicalSummary: 'Tanmay demonstrates outstanding tactile sensitivity and screen reader audio speed.',
    aiRecommendation: 'Advance Nemeth Braille math symbol cell patterns in BrailleModule and pair with screen reader hints.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-26', notes: 'Audio-guided AAC' },
      'speech-therapy': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-21', notes: 'Clear articulation' },
      'autism': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'sign-language': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'braille': { status: 'MASTERED', scorePercent: 96, lastAssessedDate: '2026-08-01', notes: 'Grade 2 Nemeth 45wpm' },
      'student': { status: 'MASTERED', scorePercent: 91, lastAssessedDate: '2026-08-01', notes: 'IEP Goal 91%' },
      'therapist': { status: 'MASTERED', scorePercent: 94, lastAssessedDate: '2026-07-30', notes: 'SOAP note complete' },
      'teacher': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-27', notes: 'Tactile math materials' },
      'assessment': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-16', notes: 'Tactile profile' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Bus Route #7' },
      'hospital': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-28', notes: 'Audio message log' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-20', notes: 'Refreshable Braille display' },
      'analytics': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-08-01', notes: 'BI report active' },
      'ai': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-30', notes: 'AI summary written' },
      'sensory-room': { status: 'MASTERED', scorePercent: 94, lastAssessedDate: '2026-07-25', notes: 'Rain audio soundscape' },
      'tele-rehab': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-26', notes: 'Audio consultation' },
      'early-intervention': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-04', notes: 'Tactile milestones' },
      'vocational-living': { status: 'IN_PROGRESS', scorePercent: 78, lastAssessedDate: '2026-07-18', notes: 'Community orientation' },
      'hydrotherapy': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'music-therapy': { status: 'MASTERED', scorePercent: 95, lastAssessedDate: '2026-07-30', notes: 'Pentatonic tone pitch 100%' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-27', notes: 'Goldendoodle AAT' },
      'prosthetics': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vbmapp-assessment': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-28', notes: 'Audio core vocab' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-16', notes: 'Cognitive FIM 32/35' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-27', notes: 'Audio neuro game' },
      'transition-matrix': { status: 'IN_PROGRESS', scorePercent: 75, lastAssessedDate: '2026-07-20', notes: 'Audio vocational matrix' }
    }
  },
  {
    id: '7',
    name: 'Miss Priya Deshmukh',
    rollId: 'STU-2026-104',
    age: 15,
    grade: 'Secondary 9-B',
    disabilityCategory: 'Post-Secondary Vocational',
    coOccurringDisabilityCategory: 'Executive Function Disorder',
    guardianName: 'Sanjay Deshmukh',
    guardianPhone: '+91 98334 55667',
    therapist: 'Vocational Specialist',
    studentEmail: 'priya.deshmukh@bugfreeschool.org',
    studentPassword: 'Priya#2026Password',
    portalPin: '7890',
    tenant: 'Sunrise Special Needs School',
    iepStatus: 'ACTIVE',
    iepGoal: 'Operate retail touchscreen register and community transit bus scanner',
    recentScore: 'Secondary Transition Matrix: 88%',
    overallMasteryPercent: 88,
    clinicalSummary: 'Priya shows strong independence in retail workplace simulations and transit card scanning.',
    aiRecommendation: 'Prepare for competitive integrated employment placement using SecondaryTransitionMatrixModule and EVVGPSModule.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-24', notes: 'Workplace phrase board' },
      'speech-therapy': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-22', notes: 'Customer service voice' },
      'autism': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'sign-language': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'student': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-08-01', notes: 'Secondary transition goal' },
      'therapist': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-30', notes: 'Vocational SOAP note' },
      'teacher': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-27', notes: 'High school curriculum' },
      'assessment': { status: 'MASTERED', scorePercent: 84, lastAssessedDate: '2026-07-15', notes: 'Vocational assessment' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Transit Bus Route #4' },
      'hospital': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'NDIS Claim approved' },
      'communication': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-28', notes: 'Workplace daily update' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-22', notes: 'POS Scanner checked out' },
      'analytics': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-08-01', notes: 'BI report active' },
      'ai': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-30', notes: 'AI resume generated' },
      'sensory-room': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-24', notes: 'Sensory de-escalation' },
      'tele-rehab': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-25', notes: 'Remote job coaching' },
      'early-intervention': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vocational-living': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-31', notes: 'POS Register & Transit' },
      'hydrotherapy': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'music-therapy': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-29', notes: 'Auditory focus music' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-27', notes: 'AAT session' },
      'prosthetics': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vbmapp-assessment': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-28', notes: 'Workplace core vocab' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 86, lastAssessedDate: '2026-07-16', notes: 'FIM Independence 118/126' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-26', notes: 'Task speed game' },
      'transition-matrix': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-08-01', notes: 'Secondary transition 88%' }
    }
  },
  {
    id: '8',
    name: 'Master Vikram Joshi',
    rollId: 'STU-2026-105',
    age: 10,
    grade: 'Pediatric Rehab Ward',
    disabilityCategory: 'Traumatic Brain Injury',
    coOccurringDisabilityCategory: 'Post-Traumatic Aphasia',
    guardianName: 'Anita Joshi',
    guardianPhone: '+91 98221 66778',
    therapist: 'Dr. Robert Vance (MD)',
    studentEmail: 'vikram.joshi@bugfreeschool.org',
    studentPassword: 'Vikram#2026Password',
    portalPin: '8901',
    tenant: 'Sunrise Special Needs School',
    iepStatus: 'ACTIVE',
    iepGoal: 'Achieve WebGL neuro-rehab reaction latency under 350ms in 10 trials',
    recentScore: 'Biofeedback Latency: 320ms Average',
    overallMasteryPercent: 81,
    clinicalSummary: 'Vikram is making significant steady recovery in motor reaction speed and cognitive attention processing.',
    aiRecommendation: 'Increase WebGL target speed in GamifiedNeuroModule and log daily inpatient ward vitals in HospitalRehabModule.',
    moduleMasteryMap: {
      'aac': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-25', notes: 'Quick phrase board' },
      'speech-therapy': { status: 'IN_PROGRESS', scorePercent: 74, lastAssessedDate: '2026-07-20', notes: 'Vocal fatigue exercise' },
      'autism': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'sign-language': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'braille': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'student': { status: 'IN_PROGRESS', scorePercent: 81, lastAssessedDate: '2026-08-01', notes: 'Neuro-rehab goal active' },
      'therapist': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-30', notes: 'Physiatrist SOAP note' },
      'teacher': { status: 'MASTERED', scorePercent: 80, lastAssessedDate: '2026-07-26', notes: 'Hospital school lesson' },
      'assessment': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-14', notes: 'TBI recovery profile' },
      'school-erp': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-08-01', notes: 'Hospital timetable' },
      'hospital': { status: 'MASTERED', scorePercent: 90, lastAssessedDate: '2026-07-31', notes: 'Bed 302-A Pediatric Neuro' },
      'billing': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'Inpatient NDIS claim' },
      'communication': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-29', notes: 'Parent medical update' },
      'inventory': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-22', notes: 'Neuro-feedback sensor' },
      'analytics': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-08-01', notes: 'BI TBI recovery curve' },
      'ai': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-30', notes: 'AI discharge summary' },
      'sensory-room': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-25', notes: 'Deep Ocean Blue Calm' },
      'tele-rehab': { status: 'MASTERED', scorePercent: 85, lastAssessedDate: '2026-07-27', notes: 'Remote neuro consultation' },
      'early-intervention': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'vocational-living': { status: 'IN_PROGRESS', scorePercent: 65, lastAssessedDate: '2026-07-18', notes: 'Rehab task analysis' },
      'hydrotherapy': { status: 'IN_PROGRESS', scorePercent: 78, lastAssessedDate: '2026-07-22', notes: 'Pool temperature 34°C' },
      'music-therapy': { status: 'MASTERED', scorePercent: 88, lastAssessedDate: '2026-07-29', notes: 'RAS beat 72 BPM' },
      'animal-therapy': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-07-28', notes: 'Goldendoodle AAT' },
      'prosthetics': { status: 'IN_PROGRESS', scorePercent: 75, lastAssessedDate: '2026-07-24', notes: 'EMG sensor gain' },
      'vbmapp-assessment': { status: 'NOT_ASSESSED', scorePercent: 0, lastAssessedDate: 'N/A', notes: 'N/A' },
      'core-vocab-ai': { status: 'MASTERED', scorePercent: 82, lastAssessedDate: '2026-07-28', notes: 'Core vocab active' },
      'fim-weefim': { status: 'MASTERED', scorePercent: 80, lastAssessedDate: '2026-07-31', notes: 'Motor FIM 72/91' },
      'evv-gps-tracker': { status: 'MASTERED', scorePercent: 100, lastAssessedDate: '2026-07-31', notes: 'EVV GPS verified' },
      'gamified-neuro': { status: 'MASTERED', scorePercent: 92, lastAssessedDate: '2026-08-01', notes: '320ms reaction latency' },
      'transition-matrix': { status: 'IN_PROGRESS', scorePercent: 60, lastAssessedDate: '2026-07-15', notes: 'Rehab matrix 60%' }
    }
  }
];

/**
 * Multi-Tenant Role-Based Data Isolation Helper
 * - STUDENT: Can ONLY see their own student record matching email/name.
 * - TENANT_ADMIN / THERAPIST / TEACHER: Can ONLY see students belonging to their tenant/school.
 * - SUPER_ADMIN: Can see ALL students across ALL tenants/schools.
 */
export const getAccessibleStudents = (currentUser?: { role: string; tenant?: string; email?: string; name?: string } | null): DemoStudent[] => {
  if (!currentUser) return DEMO_STUDENTS;

  // 1. SUPER_ADMIN: Full global access to all students across all tenants
  if (currentUser.role === 'SUPER_ADMIN') {
    return DEMO_STUDENTS;
  }

  // 2. STUDENT: Strict single-student isolation (only their own profile)
  if (currentUser.role === 'STUDENT') {
    const student = DEMO_STUDENTS.find(s => 
      (currentUser.email && s.studentEmail.toLowerCase() === currentUser.email.toLowerCase()) ||
      (currentUser.name && s.name.toLowerCase() === currentUser.name.toLowerCase())
    );
    return student ? [student] : [DEMO_STUDENTS[0]];
  }

  // 3. TENANT_ADMIN / THERAPIST / TEACHER: Strict tenant isolation (only students in their school)
  const tenantName = currentUser.tenant || 'Sunshine Autism Academy';
  const tenantStudents = DEMO_STUDENTS.filter(s => 
    !s.tenant || s.tenant.toLowerCase() === tenantName.toLowerCase()
  );

  return tenantStudents.length > 0 ? tenantStudents : DEMO_STUDENTS;
};
